import { CssBaseline, ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import React from 'react';

const lightPrimaryColor = 'rgba(236, 234, 243, 1)';
const mainPrimaryColor = 'rgba(60, 46, 133, 1)';
const mainSecondaryColor = 'rgba(8, 209, 178, 1)';
const darkSecondaryColor = 'rgba(7, 185, 158, 1)';
const mainErrorColor = 'rgb(250, 101, 101)';
const darkErrorColor = 'rgb(206, 78, 78)';

const appTheme = createTheme({
  palette: {
    primary: {
      main: mainPrimaryColor,
      light: lightPrimaryColor,
    },
    secondary: {
      main: mainSecondaryColor,
      dark: darkSecondaryColor,
    },
    background: {
      default: 'rgb(240, 242, 245)',
    },
    text: {
      primary: 'rgba(36, 32, 56, 1)',
      secondary: 'rgba(36, 32, 56, 0.8)',
    },
    grey: {
      A200: 'rgba(245,245,249)',
    },
  },
  typography: {
    fontFamily: 'Muli, sans-serif',
    h2: {
      fontSize: '50px',
      lineHeight: '50px',
      fontWeight: 800,
      letterSpacing: '-0.32px',
    },
    h5: {
      fontSize: 24,
      fontWeight: 800,
      lineHeight: '24px',
    },
    h6: {
      fontSize: 16,
      fontWeight: 800,
    },
    body1: {
      fontSize: '17px',
      fontWeight: 400,
      letterSpacing: '0.5px',
      borderRadius: '5px',
    },
  },
  components: {
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: 48,
          border: '1px solid rgba(126,126,126, 0.12)',
          '&:hover': {
            backgroundColor: lightPrimaryColor,
          },
          '&:hover .MuiTableCell-root, &:hover .MuiTableCell-root span.material-icons-outlined': {
            color: mainPrimaryColor,
          },
          '& td': {
            height: 48,
            padding: '4px 16px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          height: '48px',
          fontSize: 16,
          variants: [
            {
              props: { variant: 'modal', color: 'error' },
              style: {
                backgroundColor: mainErrorColor,
                '&:hover': {
                  backgroundColor: darkErrorColor,
                },
              },
            },
          ],
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          ...theme.typography.body1,
          borderBottom: 'none',
          borderRadius: '0px',
          color: theme.palette.text.primary,
          fontSize: '16px',
          fontStyle: 'normal',
          lineHeight: '100%',
          fontWeight:
            ownerState.variant === 'bold'
              ? theme.typography.fontWeightBold
              : theme.typography.fontWeightRegular,
        }),
        head: ({ theme }) => ({
          ...theme.typography.h6,
          backgroundColor: 'rgba(250, 250, 250, 1)',
        }),
      },
    },
  },
});

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <MuiThemeProvider theme={appTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    modal: true;
  }
}

declare module '@mui/material/TableCell' {
  interface TableCellPropsVariantOverrides {
    bold: true;
  }
}
